var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '@/styles/Board/BoardPage.less';
import { useAtom } from 'jotai';
import { useStore } from 'jotai/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useApiInstance } from '@/api';
import { createWS } from '@/api/ws';
import BoardElementToolbar from '@/components/Board/BoardElementToolbar';
import BoardStage from '@/components/Board/BoardStage';
import { MainBoardToolbar, ZoomGridToolbar } from '@/components/Board/BoardToolbar';
import NotesPanel from '@/components/Board/NotesPanel/NotesPanel';
import { PresentationMode } from '@/components/Board/PresentationMode';
import PresentationPanel from '@/components/Board/PresentationPanel';
import QRCodeModal from '@/components/Common/QRCodeModal';
import { useAuth } from '@/context/AuthContext';
import { usePresentationMode, usePresentationWebSocket, useStageViewport, useStageWheelEvents, } from '@/hooks/board';
import { useBoardHistory } from '@/hooks/useHistory';
import { boardStateAtom, gridSizeAtom, inputModeAtom, presentationBoundsAtom, presentationElementIdAtom, presentationElementsAtom, presentationModeAtom, roleAtom, selectedIdsAtom, showGridAtom, snapToGridAtom, snapToGuidesAtom, stageModeAtom, } from '@/store/boardAtoms';
import { addElement, clone, deleteElement, firstVisibleLayer, patchElement, patchLayer, selectionLockState, } from '@/utils/board/boardUtils';
import { draftStateAfterUpdate, getElementsMap } from '@/utils/board/stageUtils';
import { loadBoardSettings, loadStagePosition, loadZoom, saveBoardSettings, saveStagePosition, saveZoom, } from '@/utils/board/viewPersistence';
import { animateStageViewToCenter, calculateStageViewToCenterElement, } from '@/utils/board/viewportUtils';
import { objectsAreEqual } from '@/utils/objectUtils';
import { getElementBounds, sortPresentationElements } from '@/utils/presentation/utils';
// TODO: CLEANUP + REFACTOR
const BoardPage = ({ slug }) => {
    // API instance для работы с сервером
    const { boardApiInstance } = useApiInstance();
    // Роль пользователя на доске
    const [role, setRole] = useAtom(roleAtom);
    // Элементы презентации
    const [presentationElements, setPresentationElements] = useAtom(presentationElementsAtom);
    const [presentationMode, setPresentationMode] = useAtom(presentationModeAtom);
    const [, setPresentationElementId] = useAtom(presentationElementIdAtom);
    const [, setPresentationBounds] = useAtom(presentationBoundsAtom);
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    // Референс для хранения флага необходимости автозапуска презентации
    const shouldAutoStartPresentationRef = useRef(false);
    // Единый стейт viewport
    const { viewport, setZoom, setPosition } = useStageViewport();
    const { zoom, position: stagePosition } = viewport;
    // Показывать ли сетку
    const [showGrid, setShowGrid] = useAtom(showGridAtom);
    // Привязывать ли элементы к сетке
    const [snapToGrid, setSnapToGrid] = useAtom(snapToGridAtom);
    const [, setSnapToGuides] = useAtom(snapToGuidesAtom);
    // Режим ввода (трекпад/мышь)
    const [inputMode, setInputMode] = useAtom(inputModeAtom);
    // Размер сетки
    const [, setGridSize] = useAtom(gridSizeAtom);
    // Режим "перемещения" или "выделения"
    const [stageMode, setStageMode] = useAtom(stageModeAtom);
    // Массив id выделенных элементов
    const [selectedIds] = useAtom(selectedIdsAtom);
    // WebSocket для синхронизации
    const wsRef = useRef(null);
    // // История изменений доски
    const { boardState, setBoardState, resetBoardState } = useBoardHistory();
    // Актуальное состояние пользователя
    const { user, loading: isAuthLoading } = useAuth();
    const userRef = useRef(user);
    userRef.current = user;
    const [boardLoaded, setBoardLoaded] = useState(false);
    const [showPresentationPanel, setShowPresentationPanel] = useState(false);
    const [showNotesPanel, setShowNotesPanel] = useState(false);
    const [showQRCodeModal, setShowQRCodeModal] = useState(false);
    // Ref для callback обновления заметок в NotesPanel
    const onNoteUpdateRef = useRef(null);
    // Референс для хранения функции отмены анимации центрирования
    const cancelAnimationRef = useRef(null);
    // Используем функции зума из useStageWheelEvents
    const wheelEvents = useStageWheelEvents({
        selectedIds,
    });
    // Сохранение позиции сцены в localStorage при изменении
    useEffect(() => {
        if (!boardLoaded)
            return;
        saveStagePosition(slug, stagePosition);
    }, [boardLoaded, slug, stagePosition]);
    // Сохранение zoom в localStorage при изменении
    useEffect(() => {
        if (!boardLoaded)
            return;
        saveZoom(slug, zoom);
    }, [boardLoaded, slug, zoom]);
    // Сохранение настроек панели инструментов в localStorage при изменении
    useEffect(() => {
        if (!boardLoaded)
            return;
        saveBoardSettings(slug, {
            showGrid,
            snapToGrid,
            inputMode,
        });
    }, [boardLoaded, slug, showGrid, snapToGrid, inputMode, showNotesPanel]);
    // Обработчики для временного отключения привязок при зажатом Meta (Cmd на Mac, Win на Windows)
    useEffect(() => {
        let snapStateInitial;
        const handleKeyDown = (e) => {
            if (e.metaKey) {
                snapStateInitial = store.get(snapToGridAtom);
                setSnapToGrid(false);
                setSnapToGuides(false);
            }
        };
        const handleKeyUp = (e) => {
            if (!e.metaKey) {
                if (snapStateInitial !== undefined) {
                    setSnapToGrid(snapStateInitial);
                }
                setSnapToGuides(true);
                snapStateInitial = undefined;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /* Применить изменения, пришедшие с сервера */
    const applyServerState = useCallback((mutator, messageType) => {
        const currentBoardState = store.get(boardStateAtom);
        const { draft, fromUndo, userId } = mutator(clone(currentBoardState), true);
        const statesAreEqual = objectsAreEqual(currentBoardState, draft);
        // Если состояния равны (изменения применены ранее) - ничего не делаем
        if (!statesAreEqual) {
            const isElementAdded = messageType === 'element.added';
            if (userRef.current && userId === userRef.current.id) {
                if (!isElementAdded || (fromUndo && isElementAdded)) {
                    // Игнорируем свои же изменения, они уже применены оптимистично
                    return;
                }
            }
            setBoardState((prev) => {
                const { draft } = mutator(clone(prev), false);
                return draft;
            });
        }
    }, [setBoardState, store]);
    /* Отправить сообщение на сервер */
    const sendMessage = useCallback((message) => {
        var _a;
        (_a = wsRef.current) === null || _a === void 0 ? void 0 : _a.send(message);
    }, []);
    // Хуки для режима презентации
    const presentation = usePresentationMode({ role, send: sendMessage });
    // Хук для обработки WebSocket сообщений презентации
    const presentationWebSocket = usePresentationWebSocket({});
    // Автозапуск презентации при инициализации, если она была включена в настройках
    useEffect(() => {
        if (shouldAutoStartPresentationRef.current && boardLoaded) {
            if (role !== 'GM') {
                presentation.startPresentation();
            }
            else {
                setShowPresentationPanel(true);
            }
            shouldAutoStartPresentationRef.current = false;
        }
    }, [boardLoaded, role, presentation]);
    /* Обновление ссылки на текущее состояние доски */
    // WebSocket & инициализация доски
    useEffect(() => {
        const ws = createWS(slug);
        wsRef.current = ws;
        // Обработчик входящих сообщений от сервера
        const off = ws.on((msg) => {
            var _a, _b;
            console.log('[BoardPage] <--- WS message', msg);
            const { type, payload } = msg;
            if (!type && !payload) {
                console.error('[BoardPage] WS message without type or payload', msg);
                return;
            }
            switch (type) {
                case 'board.state.init': {
                    const initialState = payload && Array.isArray(payload.layers)
                        ? payload
                        : { layers: [] };
                    if (msg.role) {
                        setRole(msg.role);
                    }
                    else {
                        console.error('[BoardPage] WS message without role', msg);
                    }
                    setBoardState(initialState);
                    resetBoardState();
                    // Восстанавливаем позицию экрана из localStorage
                    const persistedPos = loadStagePosition(slug);
                    const persistedZoom = loadZoom(slug);
                    if (persistedPos)
                        setPosition(persistedPos);
                    if (persistedZoom !== null)
                        setZoom(persistedZoom);
                    // Инициализация элементов презентации
                    const initialPresentationElements = [];
                    for (const layer of initialState.layers || []) {
                        for (const element of layer.elements || []) {
                            if (element.isPresentationFrame) {
                                initialPresentationElements.push(element);
                            }
                        }
                    }
                    setPresentationElements(sortPresentationElements(initialPresentationElements));
                    // Восстановление настроек доски
                    const settings = payload === null || payload === void 0 ? void 0 : payload.settings;
                    if (settings) {
                        setPresentationMode(settings.isPresentationMode);
                        setShowGrid(settings.showGrid);
                        setGridSize(settings.gridSize);
                        setSnapToGrid(settings.snapToGrid);
                        // Помечаем, что нужно запустить презентацию при следующем рендере
                        if ((settings === null || settings === void 0 ? void 0 : settings.isPresentationMode) === true) {
                            shouldAutoStartPresentationRef.current = true;
                        }
                        else {
                            shouldAutoStartPresentationRef.current = false;
                        }
                    }
                    // Загрузка локальных пользовательских настроек из localStorage
                    // Эти настройки перезаписывают серверные значения, так как это пользовательские предпочтения
                    const localSettings = loadBoardSettings(slug);
                    if (localSettings) {
                        if (localSettings.showGrid !== undefined) {
                            setShowGrid(localSettings.showGrid);
                        }
                        if (localSettings.snapToGrid !== undefined) {
                            setSnapToGrid(localSettings.snapToGrid);
                        }
                        if (localSettings.inputMode !== undefined) {
                            setInputMode(localSettings.inputMode);
                        }
                    }
                    setBoardLoaded(true);
                    break;
                }
                case 'board.settings.updated': {
                    // Обновление настроек доски
                    const settings = payload;
                    if (settings) {
                        setPresentationMode(settings.isPresentationMode);
                        setShowGrid(settings.showGrid);
                        setGridSize(settings.gridSize);
                        setSnapToGrid(settings.snapToGrid);
                        // Помечаем, что нужно запустить презентацию при следующем рендере
                        if ((settings === null || settings === void 0 ? void 0 : settings.isPresentationMode) === true) {
                            shouldAutoStartPresentationRef.current = true;
                        }
                        else {
                            shouldAutoStartPresentationRef.current = false;
                        }
                    }
                    break;
                }
                case 'element.moved': {
                    // Обработка перемещения элемента
                    applyServerState((draft, _shallow) => patchElement(draft, payload, _shallow), type);
                    break;
                }
                case 'element.added': {
                    // Обработка добавления элемента
                    applyServerState((draft, _shallow) => addElement(draft, payload.layerId, payload), type);
                    break;
                }
                case 'element.updated': {
                    // Обработка обновления элемента
                    applyServerState((draft, _shallow) => patchElement(draft, payload, _shallow), type);
                    break;
                }
                case 'element.deleted': {
                    // Обработка удаления элемента
                    applyServerState((draft, _shallow) => deleteElement(draft, payload), type);
                    break;
                }
                case 'layer.updated': {
                    // Обработка обновления слоя
                    applyServerState((draft, _shallow) => patchLayer(draft, payload.id, payload), type);
                    break;
                }
                case 'presentation.change': {
                    // Владелец доски изменил презентацию
                    if (store.get(roleAtom) === 'GM') {
                        break;
                    }
                    presentationWebSocket.handlePresentationChange(payload);
                    break;
                }
                case 'note.updated': {
                    /**
                     * Обработка обновления заметки из WebSocket.
                     * Обновляет связанные текстовые элементы на доске и состояние заметок в панели.
                     */
                    const noteData = payload;
                    // Обновляем текстовые элементы на доске, если они были синхронизированы с заметкой
                    if ((_a = noteData.updatedElements) === null || _a === void 0 ? void 0 : _a.length) {
                        applyServerState((draft, _shallow) => patchElement(draft, noteData.updatedElements, _shallow), type);
                    }
                    // Обновляем состояние заметок в панели
                    (_b = onNoteUpdateRef.current) === null || _b === void 0 ? void 0 : _b.call(onNoteUpdateRef, noteData);
                    break;
                }
            }
        });
        // Очистка при размонтировании компонента
        return () => {
            off();
            wsRef.current = null;
        };
    }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
    // Обработчики для презентационных элементов
    const handleTogglePresentationFrame = useCallback((isPresentationFrame) => {
        if (selectedIds.length === 0)
            return;
        const updates = [];
        const currentBoardState = store.get(boardStateAtom);
        if (isPresentationFrame) {
            // При добавлении элементов в список презентационных фреймов
            // Находим максимальный presentationOrder среди всех существующих презентационных элементов
            const allPresentationElements = currentBoardState.layers
                .flatMap((layer) => layer.elements)
                .filter((el) => el.isPresentationFrame);
            const maxOrder = allPresentationElements.reduce((max, el) => Math.max(max, el.presentationOrder || 0), -1);
            // Для каждого добавляемого элемента устанавливаем presentationOrder = maxOrder + 1 + индекс
            selectedIds.forEach((id, idx) => {
                const presentationOrder = maxOrder + 1 + idx;
                updates.push({ id, isPresentationFrame, presentationOrder });
            });
        }
        else {
            // При удалении просто сбрасываем флаг
            for (const id of selectedIds) {
                updates.push({ id, isPresentationFrame });
            }
        }
        setBoardState((prev) => draftStateAfterUpdate(prev, updates));
        sendMessage({ type: 'element.update', payload: updates });
        setPresentationElements((prev) => {
            const newElements = [...prev];
            for (const update of updates) {
                const index = newElements.findIndex((el) => el.id === update.id);
                if (isPresentationFrame) {
                    // Добавляем в презентационные элементы
                    if (index === -1) {
                        const element = currentBoardState.layers
                            .flatMap((layer) => layer.elements)
                            .find((el) => el.id === update.id);
                        if (element) {
                            // Обновляем элемент с правильным presentationOrder
                            const updatedElement = Object.assign(Object.assign({}, element), { isPresentationFrame: true, presentationOrder: update.presentationOrder || 0 });
                            newElements.push(updatedElement);
                        }
                    }
                }
                else {
                    // Убираем из презентационных элементов
                    if (index !== -1) {
                        newElements.splice(index, 1);
                    }
                }
            }
            return sortPresentationElements(newElements);
        });
    }, [selectedIds, store, setBoardState, sendMessage, setPresentationElements]);
    const handleSetActivePresentationFrame = useCallback((element) => {
        var _a;
        // Получаем актуальный элемент из boardState, чтобы учесть последние изменения позиции
        const currentBoardState = store.get(boardStateAtom);
        const elementsMap = getElementsMap(currentBoardState.layers);
        const actualElement = (_a = elementsMap.get(element.id)) === null || _a === void 0 ? void 0 : _a.el;
        if (!actualElement)
            return;
        // Обновляем признак активного фрейма у всех презентационных элементов
        const updates = presentationElements.map((el) => ({
            id: el.id,
            isActivePresentationFrame: el.id === element.id,
        }));
        setBoardState((prev) => draftStateAfterUpdate(prev, updates));
        sendMessage({ type: 'element.update', payload: updates });
        // Локально помечаем активный фрейм
        setPresentationElements((prev) => prev.map((el) => (Object.assign(Object.assign({}, el), { isActivePresentationFrame: el.id === element.id }))));
        // Переключаем текущий элемент презентации
        // Используем актуальный элемент из boardState для получения правильных границ
        setPresentationElementId(actualElement.id);
        const bounds = getElementBounds(actualElement);
        setPresentationBounds(bounds);
        // Отправляем зрителям смену кадра
        sendMessage({
            type: 'presentation.change',
            payload: { elementId: actualElement.id, bounds },
        });
    }, [
        store,
        presentationElements,
        setBoardState,
        sendMessage,
        setPresentationElements,
        setPresentationElementId,
        setPresentationBounds,
    ]);
    const handlePrevPresentationFrame = useCallback(() => {
        if (!presentationElements.length)
            return;
        const sorted = sortPresentationElements(presentationElements);
        const currentIndex = sorted.findIndex((el) => el.isActivePresentationFrame);
        const targetIndex = currentIndex <= 0 ? 0 : currentIndex - 1;
        if (currentIndex !== targetIndex) {
            handleSetActivePresentationFrame(sorted[targetIndex]);
        }
    }, [presentationElements, handleSetActivePresentationFrame]);
    const handleNextPresentationFrame = useCallback(() => {
        if (!presentationElements.length)
            return;
        const sorted = sortPresentationElements(presentationElements);
        const currentIndex = sorted.findIndex((el) => el.isActivePresentationFrame);
        const targetIndex = currentIndex === -1 ? 0 : Math.min(sorted.length - 1, currentIndex + 1);
        if (currentIndex !== targetIndex) {
            handleSetActivePresentationFrame(sorted[targetIndex]);
        }
    }, [presentationElements, handleSetActivePresentationFrame]);
    const handleUpdateElementName = useCallback((elementId, newName) => {
        const updates = [{ id: elementId, name: newName }];
        setBoardState((prev) => draftStateAfterUpdate(prev, updates));
        sendMessage({ type: 'element.update', payload: updates });
        // Обновляем локальное состояние элементов презентации
        setPresentationElements((prev) => prev.map((el) => (el.id === elementId ? Object.assign(Object.assign({}, el), { name: newName }) : el)));
    }, [setBoardState, sendMessage, setPresentationElements]);
    const handleReorderPresentationElements = useCallback((elements) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            // Обновляем локальное состояние
            setPresentationElements(sortPresentationElements(elements));
            const payload = elements.map(({ id, presentationOrder }) => ({ id, presentationOrder }));
            yield boardApiInstance.reorderPresentationElements(slug, payload);
        }
        catch (error) {
            console.error('Ошибка при изменении порядка презентационных элементов:', error);
        }
    }), [boardApiInstance, slug, setPresentationElements]);
    /**
     * Центрирует вид стейджа на элементе с плавной анимацией
     * Вычисляет оптимальную позицию стейджа и зум для отображения элемента в центре viewport
     * и плавно перемещает вид от текущей позиции к целевой
     */
    const handleCenterOnElement = useCallback((element) => {
        // Отменяем предыдущую анимацию, если она есть
        if (cancelAnimationRef.current) {
            cancelAnimationRef.current();
            cancelAnimationRef.current = null;
        }
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const viewConfig = calculateStageViewToCenterElement(element, viewportWidth, viewportHeight);
        if (!viewConfig) {
            return;
        }
        // Запускаем плавную анимацию перехода от текущей позиции/зума к целевой
        const cancelAnimation = animateStageViewToCenter({
            currentPosition: stagePosition,
            currentZoom: zoom,
            targetPosition: viewConfig.stagePosition,
            targetZoom: viewConfig.zoom,
            onPositionUpdate: setPosition,
            onZoomUpdate: setZoom,
        });
        // Сохраняем функцию отмены анимации
        cancelAnimationRef.current = cancelAnimation;
    }, [stagePosition, zoom, setPosition, setZoom]);
    // Очистка анимации при размонтировании компонента
    useEffect(() => {
        return () => {
            if (cancelAnimationRef.current) {
                cancelAnimationRef.current();
            }
        };
    }, []);
    /* Добавление фигуры (прямоугольник или круг) */
    const handleAddShape = useCallback((shape) => {
        const firstLayer = firstVisibleLayer(store.get(boardStateAtom));
        console.log('[handleAddShape] firstLayer', firstLayer);
        if (!firstLayer)
            return;
        // Вставка по центру текущего экрана пользователя с учётом зума и смещения сцены
        const viewportCenterX = window.innerWidth / 2;
        const viewportCenterY = window.innerHeight / 2;
        const sceneCenterX = (viewportCenterX - stagePosition.x) / zoom;
        const sceneCenterY = (viewportCenterY - stagePosition.y) / zoom;
        const defaultSize = shape === 'rect' ? { w: 120, h: 80 } : { w: 80, h: 80 };
        const x = sceneCenterX - defaultSize.w / 2;
        const y = sceneCenterY - defaultSize.h / 2;
        const props = shape === 'rect'
            ? {
                x,
                y,
                w: defaultSize.w,
                h: defaultSize.h,
                fill: '#fff',
                stroke: '#333',
                shape: 'rect',
            }
            : {
                x,
                y,
                w: defaultSize.w,
                h: defaultSize.h,
                fill: '#fff',
                stroke: '#333',
                shape: 'circle',
            };
        sendMessage({
            type: 'element.add',
            payload: { layerId: firstLayer.id, elementType: 'shape', props },
        });
    }, [sendMessage, stagePosition.x, stagePosition.y, store, zoom]);
    /* Добавление текста */
    const handleAddText = useCallback(() => {
        const firstLayer = firstVisibleLayer(store.get(boardStateAtom));
        if (!firstLayer)
            return;
        // Вставка по центру текущего экрана пользователя с учётом зума и смещения сцены
        const viewportCenterX = window.innerWidth / 2;
        const viewportCenterY = window.innerHeight / 2;
        const sceneCenterX = (viewportCenterX - stagePosition.x) / zoom;
        const sceneCenterY = (viewportCenterY - stagePosition.y) / zoom;
        const defaultSize = { w: 300, h: 100 };
        const x = sceneCenterX - defaultSize.w / 2;
        const y = sceneCenterY - defaultSize.h / 2;
        const props = {
            x,
            y,
            w: defaultSize.w,
            h: defaultSize.h,
            text: 'Your text here',
            fontSize: 30,
            textAlign: 'center',
            verticalAlign: 'middle',
            textColor: '#000000',
            backgroundColor: '#ffffff',
        };
        sendMessage({
            type: 'element.add',
            payload: { layerId: firstLayer.id, elementType: 'text', props },
        });
    }, [sendMessage, stagePosition.x, stagePosition.y, store, zoom]);
    const handleApplyMask = (mask) => {
        if (!mask || selectedIds.length === 0)
            return;
        const propsForMask = mask === 'none'
            ? { clip: null }
            : mask === 'circle'
                ? { clip: { type: 'circle' } }
                : { clip: { type: 'rect' } };
        const updates = [];
        for (const id of selectedIds) {
            updates.push({ id: id, props: propsForMask });
        }
        if (updates.length === 0)
            return;
        setBoardState(draftStateAfterUpdate(boardState, updates));
        sendMessage({ type: 'element.update', payload: updates });
    };
    // Debounced функция для отправки изменений сетки на сервер
    const debouncedSendGridUpdate = useDebouncedCallback((updates) => {
        sendMessage({ type: 'element.update', payload: updates });
    }, 1000);
    const handleLocalGridChange = (localGrid) => {
        if (!localGrid)
            return;
        const { enabled = false, size = 40, color = '#ffffffff' } = localGrid;
        if (selectedIds.length === 0)
            return;
        const updates = [];
        for (const id of selectedIds) {
            updates.push({
                id,
                props: { localGrid: { enabled, size, color } },
            });
        }
        setBoardState(draftStateAfterUpdate(boardState, updates));
        debouncedSendGridUpdate(updates);
    };
    /**
     * Создает массив обновлений для фильтров элементов.
     */
    const createFiltersUpdates = useCallback((filters) => {
        return selectedIds.map((id) => ({ id, props: { filters } }));
    }, [selectedIds]);
    /**
     * Обработчик изменения фильтров изображения (немедленное обновление для визуализации)
     */
    const handleFiltersChange = useCallback((filters) => {
        if (selectedIds.length === 0)
            return;
        const updates = createFiltersUpdates(filters);
        setBoardState((prevState) => draftStateAfterUpdate(prevState, updates));
    }, [createFiltersUpdates, selectedIds.length, setBoardState]);
    // Debounced функция для отправки изменений фильтров на сервер
    const debouncedSendFiltersUpdate = useDebouncedCallback((filters) => {
        if (selectedIds.length === 0)
            return;
        const updates = createFiltersUpdates(filters);
        sendMessage({ type: 'element.update', payload: updates });
    }, 300);
    /**
     * Обработчик изменения цвета заливки фигуры (немедленное обновление для визуализации)
     */
    const handleFillChange = useCallback((fill) => {
        if (selectedIds.length === 0)
            return;
        const updates = selectedIds.map((id) => ({
            id,
            props: { fill },
        }));
        setBoardState((prevState) => draftStateAfterUpdate(prevState, updates));
    }, [selectedIds, setBoardState]);
    // Debounced функция для отправки изменений цвета на сервер
    const debouncedSendFillUpdate = useDebouncedCallback((fill) => {
        if (selectedIds.length === 0)
            return;
        const updates = selectedIds.map((id) => ({
            id,
            props: { fill },
        }));
        sendMessage({ type: 'element.update', payload: updates });
    }, 300);
    /**
     * Debounced обработчик изменения прозрачности фигуры
     */
    const handleOpacityChange = useCallback((opacity) => {
        if (selectedIds.length === 0)
            return;
        const updates = selectedIds.map((id) => ({
            id,
            props: { opacity },
        }));
        setBoardState((prevState) => draftStateAfterUpdate(prevState, updates));
    }, [selectedIds, setBoardState]);
    // Debounced функция для отправки изменений прозрачности на сервер
    const debouncedSendOpacityUpdate = useDebouncedCallback((opacity) => {
        if (selectedIds.length === 0)
            return;
        const updates = selectedIds.map((id) => ({
            id,
            props: { opacity },
        }));
        sendMessage({ type: 'element.update', payload: updates });
    }, 300);
    /**
     * Обработчик изменения цвета обводки
     */
    const handleStrokeChange = useCallback((stroke) => {
        if (selectedIds.length === 0)
            return;
        const updates = selectedIds.map((id) => ({
            id,
            props: { stroke },
        }));
        setBoardState((prevState) => draftStateAfterUpdate(prevState, updates));
    }, [selectedIds, setBoardState]);
    // Debounced функция для отправки изменений цвета обводки на сервер
    const debouncedSendStrokeUpdate = useDebouncedCallback((stroke) => {
        if (selectedIds.length === 0)
            return;
        const updates = selectedIds.map((id) => ({
            id,
            props: { stroke },
        }));
        sendMessage({ type: 'element.update', payload: updates });
    }, 300);
    /**
     * Обработчик изменения ширины обводки
     */
    const handleStrokeWidthChange = useCallback((strokeWidth) => {
        if (selectedIds.length === 0)
            return;
        const updates = selectedIds.map((id) => ({
            id,
            props: { strokeWidth },
        }));
        setBoardState((prevState) => draftStateAfterUpdate(prevState, updates));
    }, [selectedIds, setBoardState]);
    // Debounced функция для отправки изменений ширины обводки на сервер
    const debouncedSendStrokeWidthUpdate = useDebouncedCallback((strokeWidth) => {
        if (selectedIds.length === 0)
            return;
        const updates = selectedIds.map((id) => ({
            id,
            props: { strokeWidth },
        }));
        sendMessage({ type: 'element.update', payload: updates });
    }, 300);
    /**
     * Обработчик включения/выключения обводки
     */
    const handleStrokeEnabledChange = useCallback((strokeEnabled) => {
        if (selectedIds.length === 0)
            return;
        const updates = selectedIds.map((id) => ({
            id,
            props: { strokeEnabled },
        }));
        setBoardState((prevState) => draftStateAfterUpdate(prevState, updates));
        sendMessage({ type: 'element.update', payload: updates });
    }, [selectedIds, setBoardState, sendMessage]);
    /**
     * Заблокировать/разблокировать выделенные элементы (только GM)
     */
    const handleToggleLock = (lock) => {
        if (role !== 'GM')
            return;
        const payload = [];
        selectedIds.forEach((id) => {
            payload.push({ id: id, locked: lock });
        });
        setBoardState(draftStateAfterUpdate(boardState, payload));
        sendMessage({ type: 'element.update', payload });
    };
    if (!boardLoaded || isAuthLoading) {
        return _jsx("div", { children: "Loading..." });
    }
    return (_jsxs("div", { className: "board-page-container", style: {
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100vw',
            // disable scroll on the whole page container
            overflow: 'hidden',
            overscrollBehavior: 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        }, children: [role === 'GM' && (_jsxs("div", { className: "toolbars", style: {
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    gridTemplateRows: 'auto auto',
                    rowGap: 8,
                    padding: '10px 14px',
                    pointerEvents: 'none',
                    width: '100%',
                    height: '100%',
                    // border: '1px solid red',
                }, children: [_jsxs("div", { style: { pointerEvents: 'none', gridColumn: '3 / 4', gridRow: 1 }, children: [showPresentationPanel && (_jsx(PresentationPanel, { presentationElements: presentationElements, onReorderPresentation: handleReorderPresentationElements, onSetActiveFrame: handleSetActivePresentationFrame, onUpdateElementName: handleUpdateElementName, onPrevFrame: handlePrevPresentationFrame, onNextFrame: handleNextPresentationFrame, presentationMode: presentation.presentationMode, onStartPresentation: presentation.startPresentation, onStopPresentation: presentation.stopPresentation, onCenterOnElement: handleCenterOnElement })), showNotesPanel && (_jsx(NotesPanel, { onNoteUpdate: (callback) => {
                                    onNoteUpdateRef.current = callback;
                                }, sendNoteUpdate: (note) => {
                                    sendMessage({
                                        type: 'note.update',
                                        payload: note,
                                    });
                                }, onClose: () => setShowNotesPanel(false) }))] }), _jsxs("div", { style: {
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr auto',
                            columnGap: 8,
                            alignItems: 'end',
                            gridColumn: '1 / 4',
                            gridRow: 2,
                            pointerEvents: 'none',
                        }, children: [_jsx(ZoomGridToolbar, { zoom: zoom, onZoomIn: wheelEvents.zoomIn, onZoomOut: wheelEvents.zoomOut, onZoomReset: wheelEvents.zoomReset, showGrid: showGrid, onShowGrid: setShowGrid, snapToGrid: snapToGrid, onSnapToGrid: setSnapToGrid, inputMode: inputMode, onInputModeChange: setInputMode, isNotesOpen: showNotesPanel, onToggleNotesPanel: () => setShowNotesPanel(!showNotesPanel), inline: true }), _jsx("div", { style: { flex: 1, display: 'flex', justifyContent: 'center' }, children: _jsx(BoardElementToolbar, { role: role, onSetMask: handleApplyMask, onToggleLock: handleToggleLock, onLocalGridChange: handleLocalGridChange, onTogglePresentationFrame: handleTogglePresentationFrame, onFiltersChange: handleFiltersChange, onFiltersChangeDebounced: debouncedSendFiltersUpdate, onFillChange: (fill) => {
                                        handleFillChange(fill);
                                        debouncedSendFillUpdate(fill);
                                    }, onOpacityChange: (opacity) => {
                                        handleOpacityChange(opacity);
                                        debouncedSendOpacityUpdate(opacity);
                                    }, onStrokeChange: (stroke) => {
                                        handleStrokeChange(stroke);
                                        debouncedSendStrokeUpdate(stroke);
                                    }, onStrokeWidthChange: (strokeWidth) => {
                                        handleStrokeWidthChange(strokeWidth);
                                        debouncedSendStrokeWidthUpdate(strokeWidth);
                                    }, onStrokeEnabledChange: handleStrokeEnabledChange, selectionLocked: selectionLockState(boardState, selectedIds), onUpdateElementName: handleUpdateElementName, styles: { pointerEvents: 'auto' } }) }), _jsx(MainBoardToolbar, { role: role, stageMode: stageMode, onStageModeChange: setStageMode, onAddShape: handleAddShape, onAddText: handleAddText, showPresentationPanel: showPresentationPanel, onTogglePresentationPanel: () => setShowPresentationPanel(!showPresentationPanel), onShowQRCode: () => setShowQRCodeModal(true), isPresentationActive: presentationMode, inline: true })] })] })), role !== 'VIEWER' && _jsx(BoardStage, { send: sendMessage }), role === 'VIEWER' && presentationMode && _jsx(PresentationMode, { send: sendMessage }), _jsx(QRCodeModal, { isOpen: showQRCodeModal || (role === 'VIEWER' && !presentationMode), onClose: () => setShowQRCodeModal(false) })] }));
};
export default BoardPage;
